import { type CheckboxProps, isWeb, styled, Checkbox as TamaguiCheckbox, YStack } from "tamagui";
import { Check as CheckIcon } from "@tamagui/lucide-icons";
import { radiusTokens } from "../themes/token-radius";
import { borderSizeTokens } from "../themes/token-space";

/**
 * Checkbox component with built-in styling and a checkmark icon
 */
const borderWidth = borderSizeTokens.border2;
const borderRadius = radiusTokens.br1;
const StyledCheckbox = styled(TamaguiCheckbox, {
  name: "Checkbox",
  width: "$si3",
  height: "$si3",
  borderRadius: borderRadius,
  borderWidth: borderWidth,
  overflow: "hidden",
  justifyContent: "center",
  alignItems: "center",
  padding: borderWidth,
  backgroundColor: "$textPrimary",
  variants: {
    checked: {
      true: {
        borderColor: "$textPrimary"
      },
      false: {
        borderColor: "$textDisabled"
      }
    },
    disabled: {
      true: {
        backgroundColor: "$uiIconFill",
        borderColor: "$uiIconFill"
      }
    }
  } as const,
  defaultVariants: {
    checked: false,
    disabled: false
  },
  transition: "bouncy",
  animateOnly: ["borderColor"]
});
const Inner = styled(YStack, {
  name: "CheckboxInner",
  position: "absolute",
  inset: isWeb ? borderWidth : 0,
  borderRadius: borderRadius - borderWidth,
  backgroundColor: "$surfaceWhite",
  variants: {
    checked: {
      true: {
        scale: 0
      },
      false: {
        scale: 1
      }
    },
    disabled: {
      true: {
        backgroundColor: "$surfaceSecondary"
      }
    }
  } as const,
  defaultVariants: {
    checked: false,
    disabled: false
  },
  transition: "quick"
});
const StyledCheckIcon = styled(CheckIcon, {
  size: "$iconSmall",
  variants: {
    disabled: {
      true: {
        color: "$surfaceSecondary"
      },
      false: {
        color: "$surfaceWhite"
      }
    }
  } as const,
  defaultVariants: {
    disabled: false
  }
});
type Props = Omit<CheckboxProps, "checked"> & {
  checked?: boolean;
};
export const Checkbox = ({
  checked,
  ...props
}: Props) => {
  const isChecked = checked === true;
  return <StyledCheckbox checked={isChecked} transition={["bouncy", {
    delay: 500
  }]} {...props} data-sentry-element="StyledCheckbox" data-sentry-component="Checkbox" data-sentry-source-file="Checkbox.tsx">
      <Inner checked={isChecked} disabled={props.disabled} transition={[isChecked ? "quick" : "bouncy", {
      delay: !isChecked ? 0 : 120
    }]} data-sentry-element="Inner" data-sentry-source-file="Checkbox.tsx" />
      <YStack justifyContent="center" alignItems="center" scale={isChecked ? 1 : 0.7} opacity={isChecked ? 1 : 0} transition={[isChecked ? "bouncy" : "quick", {
      delay: isChecked ? 120 : 0
    }]} animateOnly={["transform", "opacity"]} pointerEvents="none" data-sentry-element="YStack" data-sentry-source-file="Checkbox.tsx">
        <StyledCheckIcon disabled={props.disabled} data-sentry-element="StyledCheckIcon" data-sentry-source-file="Checkbox.tsx" />
      </YStack>
    </StyledCheckbox>;
};